//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { ILike2BuyCustomizations } from '@like2buy-v2/like2BuyV2UIUtils';
import FileId from '@models/FileId';
import { SupportedImageMimeTypes } from '@utils/mimeTypes';

export const INSTAGRAM_PAGE_SCROLL_SELECTOR = '.cu-page-frame-scroll-wrap';

export const UPLOAD_BYTES_LIMIT = 5 * 1024 * 1024; // 5mb
export const SUPPORTED_IMAGE_UPLOAD_FILE_TYPES: SupportedImageMimeTypes[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
];

export const BANNER_CONSTANTS = {
  destinationUrl: {
    inputName: 'destination-url',
    placeholder: 'https://www.yoursite.com',
    title: 'Banner Link',
    titleHelpText:
      'This link will open when a user selects your banner image. Tracking parameters based on your settings will be added to the link.',
    validationErrorMessage: 'Must be a valid URL.',
  },
  bannerImageUpload: {
    // TODO [PD-223673]: Change once size is increased in backend
    fileSizeErrorMessage: 'Images must be smaller than 5MB.',
    imageUploadedMessage: 'Image uploaded',
    inputId: 'banner-image-upload-input',
    errorId: 'banner-image-upload-error',
    inputName: 'banner-image-upload',
    noImageUploadedMessage: 'No image uploaded',
    removeBannerTooltip: 'Remove your uploaded banner image.',
    title: 'Banner Image',
    titleHelpText:
      'We recommend a resolution of 1260x315 pixels (4:1 aspect ratio) for your banner. Images must be smaller than 5MB and one of these file types: .jpeg, .png, or .webp.',
    validationErrorMessage: 'Must add a banner image.',
  },
  bannerDesktopImageUpload: {
    fileSizeErrorMessage: 'Images must be smaller than 5MB.',
    imageUploadedMessage: 'Image uploaded',
    inputId: 'banner-desktop-image-upload-input',
    errorId: 'banner-desktop-image-upload-error',
    inputName: 'banner-desktop-image-upload',
    noImageUploadedMessage: 'No image uploaded',
    removeBannerTooltip: 'Remove your uploaded desktop banner image.',
    title: 'Desktop Banner Image (Optional)',
    titleHelpText:
      'We recommend a resolution of 1260x315 pixels (4:1 aspect ratio) for your banner. Images must be smaller than 5MB and one of these file types: .jpeg, .png, or .webp.',
  },
  bannerAltText: {
    inputName: 'banner-alt-text',
    title: 'Banner Alt Text (Optional)',
    titleHelpText:
      'Add descriptive alt text to provide users using screen readers with context for your banner image.',
    ctaLimit: 120,
  },
  desktopBannerAltText: {
    inputName: 'banner-desktop-alt-text',
    title: 'Desktop Banner Alt Text (Optional)',
    titleHelpText:
      'Add descriptive alt text to provide users using screen readers with context for your desktop banner image.',
    ctaLimit: 120,
  },
} as const;

export const CTA_CONSTANTS = {
  galleryHeader: {
    inputName: 'gallery-header-cta',
    placeholder: 'Select a post for more information',
    title: 'Homepage',
    subtitle: 'Default: Select a post for more information',
    ctaLimit: 60,
  },
  desktopGalleryHeader: {
    inputName: 'desktop-gallery-header-cta',
    placeholder: 'Select a post for more information',
    title: 'Homepage - Desktop (Optional)',
    subtitle: 'Default: Select a post for more information',
    ctaLimit: 60,
  },
  galleryMediaHover: {
    inputName: 'gallery-media-hover-cta',
    placeholder: 'More Info',
    title: 'Media Hover (Desktop)',
    subtitle: 'Default: More Info',
    ctaLimit: 40,
  },
  productsContainer: {
    inputName: 'products-container-cta',
    placeholder: 'Featured in post',
    title: 'Product Page',
    subtitle: 'Default: Featured in post',
    ctaLimit: 60,
  },
} as const;

export const EMAIL_CAPTURE_CONSTANTS = {
  cta: {
    inputName: 'email-capture-cta',
    placeholder: 'Sign up to stay connected!',
    title: 'Call to Action',
    subtitle: 'Default: Sign up to stay connected!',
    ctaLimit: 60,
  },
  termsAndConditionsRequired: {
    inputName: 'email-capture-terms-and-conditions-required',
    title: 'Require Opt-in to Email Capture',
    subtitle:
      "Require users to confirm they've read your terms and conditions.",
  },
  termsAndConditionsUrl: {
    missingErrorMessage: 'Must provide a valid URL.',
    inputName: 'email-capture-terms-and-conditions-url',
    placeholder: 'https://www.example.com',
    title: 'Terms and Conditions Link',
    titleHelpText:
      'Add a terms and conditions link to include on your email capture form.',
    validationErrorMessage: 'Must be a valid URL.',
  },
} as const;

export const GALLERY_SQUARE_THUMBNAILS_CONSTANTS = {
  isEnabled: {
    inputName: 'gallery-square-thumbnails-is-enabled',
    title: 'Square Thumbnails',
    subtitle:
      'Crop your post thumbnails to squares for a more uniform store grid.',
  },
} as const;

export const GALLERY_SKIP_DETAILS_FOR_SINGLE_PRODUCT_CONSTANTS = {
  isEnabled: {
    inputName: 'gallery-skip-details-for-single-product-is-enabled',
    title: 'Skip Details',
    subtitle:
      'Skip the product details page when a post is only tagged with one product.',
  },
};

export function getExceededCtaLimitWarning(limit: number) {
  return `Please enter ${limit} characters or less.`;
}

export interface IInstagramLike2BuyV2TextCustomization {
  text: string;
}

export interface IInstagramLike2BuyV2Customizations {
  banner: {
    altText: IInstagramLike2BuyV2TextCustomization;
    desktopAltText: IInstagramLike2BuyV2TextCustomization;
    crawlId?: string;
    destinationUrl: IInstagramLike2BuyV2TextCustomization;
    imageUpload: {
      imageFileName?: string;
      imageFileId?: FileId;
    };
    desktopImageUpload: {
      desktopImageFileName?: string;
      desktopImageFileId?: FileId;
    };
    isBannerEnabled: boolean;
  };
  ctas: {
    galleryHeader: IInstagramLike2BuyV2TextCustomization;
    desktopGalleryHeader: IInstagramLike2BuyV2TextCustomization;
    galleryMediaHover: IInstagramLike2BuyV2TextCustomization;
    productsContainer: IInstagramLike2BuyV2TextCustomization;
  };
  emailCapture: {
    cta: IInstagramLike2BuyV2TextCustomization;
    isEnabled: boolean;
    shouldRequireTermsAndConditions: boolean;
    termsAndConditionsUrl: IInstagramLike2BuyV2TextCustomization;
  };
  gallerySquareThumbnails: {
    isEnabled: boolean;
  };
  gallerySkipDetailsForSingleProduct: {
    isEnabled: boolean;
  };
}

export const DEFAULT_CTA_CUSTOMIZATIONS: IInstagramLike2BuyV2Customizations['ctas'] =
  {
    galleryHeader: {
      text: '',
    },
    desktopGalleryHeader: {
      text: '',
    },
    galleryMediaHover: {
      text: '',
    },
    productsContainer: {
      text: '',
    },
  };

export const DEFAULT_BANNER_CUSTOMIZATIONS: IInstagramLike2BuyV2Customizations['banner'] =
  {
    altText: {
      text: '',
    },
    desktopAltText: {
      text: '',
    },
    destinationUrl: {
      text: '',
    },
    imageUpload: {},
    desktopImageUpload: {},
    isBannerEnabled: false,
  };

export const DEFAULT_EMAIL_CAPTURE_CUSTOMIZATIONS: IInstagramLike2BuyV2Customizations['emailCapture'] =
  {
    cta: {
      text: '',
    },
    isEnabled: false,
    shouldRequireTermsAndConditions: false,
    termsAndConditionsUrl: {
      text: '',
    },
  };

export const DEFAULT_GALLERY_SQUARE_THUMBNAILS: IInstagramLike2BuyV2Customizations['gallerySquareThumbnails'] =
  {
    isEnabled: false,
  };

export const DEFAULT_GALLERY_SKIP_DETAILS_FOR_SINGLE_PRODUCT: IInstagramLike2BuyV2Customizations['gallerySkipDetailsForSingleProduct'] =
  {
    isEnabled: false,
  };

export const DEFAULT_INSTAGRAM_CUSTOMIZATIONS: ILike2BuyCustomizations = {
  ctas: DEFAULT_CTA_CUSTOMIZATIONS,
  banner: DEFAULT_BANNER_CUSTOMIZATIONS,
  emailCapture: DEFAULT_EMAIL_CAPTURE_CUSTOMIZATIONS,
  gallerySquareThumbnails: DEFAULT_GALLERY_SQUARE_THUMBNAILS,
  gallerySkipDetailsForSingleProduct:
    DEFAULT_GALLERY_SKIP_DETAILS_FOR_SINGLE_PRODUCT,
};
